import type { FC, ReactNode } from 'react'
import type { HeadingSegment } from '@/components/generals/animated-heading'

import { useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Section } from '@/components/generals/section'
import { SectionHeading } from '@/components/generals/section-heading'
import { faqs } from '@/constants/content'
import { gsap, EASE, registerGsap, prefersReducedMotion, useIsomorphicLayoutEffect } from '@/lib/motion'

type FaqItemProps = {
    question: string
    answer: string
    open: boolean
    onToggle: () => void
    index: number
}

const FaqItem = ({ question, answer, open, onToggle, index }: FaqItemProps) => {
    const panelRef = useRef<HTMLDivElement | null>(null)

    useIsomorphicLayoutEffect(() => {
        const panel = panelRef.current

        if (!panel) {
            return
        }

        if (prefersReducedMotion()) {
            gsap.set(panel, { height: open ? 'auto' : 0, opacity: open ? 1 : 0 })

            return
        }

        registerGsap()

        const context = gsap.context(() => {
            gsap.to(panel, {
                height: open ? 'auto' : 0,
                opacity: open ? 1 : 0,
                duration: open ? 0.5 : 0.35,
                ease: EASE.enter
            })
        }, panel)

        return () => context.revert()
    }, [open])

    return (
        <div className='overflow-hidden rounded-xl bg-surface-1 ring-1 ring-white/8 transition-colors duration-500 ease-[cubic-bezier(0.44,0,0.56,1)] hover:ring-white/16'>
            <h3>
                <button
                    type='button'
                    id={`faq-trigger-${index}`}
                    aria-expanded={open}
                    aria-controls={`faq-panel-${index}`}
                    onClick={onToggle}
                    className='flex w-full cursor-pointer items-center justify-between gap-5 px-5 py-5 text-left sm:px-6'
                >
                    <span className='text-[15px] leading-[1.4] font-medium tracking-[-0.02em] text-white sm:text-base'>
                        {question}
                    </span>

                    <span
                        aria-hidden='true'
                        className={cn(
                            'relative flex size-6 shrink-0 items-center justify-center rounded-full ring-1 transition-all duration-500 ease-[cubic-bezier(0.44,0,0.56,1)]',
                            open ? 'bg-[#8b5cf6] text-white ring-[#8b5cf6]' : 'bg-white/6 text-white/70 ring-white/10'
                        )}
                    >
                        <span className='absolute h-px w-2.5 bg-current' />

                        <span
                            className={cn(
                                'absolute h-2.5 w-px bg-current transition-transform duration-500 ease-[cubic-bezier(0.44,0,0.56,1)]',
                                open ? 'scale-y-0' : 'scale-y-100'
                            )}
                        />
                    </span>
                </button>
            </h3>

            <div
                id={`faq-panel-${index}`}
                ref={panelRef}
                role='region'
                aria-labelledby={`faq-trigger-${index}`}
                className='overflow-hidden'
                style={{ height: 0, opacity: 0 }}
            >
                <p className='px-5 pb-5 text-[15px] leading-[1.6] tracking-[-0.01em] text-white/56 sm:px-6'>
                    {answer}
                </p>
            </div>
        </div>
    )
}

type FaqProps = {
    eyebrow?: string
    segments?: HeadingSegment[]
}

export const Faq: FC<FaqProps> = ({ eyebrow, segments }): ReactNode => {
    const [openIndex, setOpenIndex] = useState<number | null>(null)

    return (
        <Section
            id='faq'
            containerClassName='max-w-[860px]'
        >
            {segments ? (
                <SectionHeading
                    eyebrow={eyebrow}
                    segments={segments}
                    className='mb-12 lg:mb-16'
                />
            ) : null}

            <Reveal
                y={24}
                blur={10}
                stagger={0.07}
                className='flex w-full flex-col gap-3'
            >
                {faqs.map((faq, index) => (
                    <FaqItem
                        key={faq.question}
                        index={index}
                        question={faq.question}
                        answer={faq.answer}
                        open={openIndex === index}
                        onToggle={() => setOpenIndex((current) => (current === index ? null : index))}
                    />
                ))}
            </Reveal>
        </Section>
    )
}
